import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Truck, Warning } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { istYYMMDD } from '@/lib/ist'
import { newId } from '@/lib/ids'
import { enqueueCommands, nextDailyCounter, useDeviceInfo, type EnqueueInput } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  IntegerInput,
  KeyValue,
  PhotoCapture,
  PickerSheet,
  Stepper,
  useToast,
} from '@/ui'
import type { DispatchShipmentPayload, LoadBagsPayload, ScanRefusal } from '../commands'
import '../commands'
import type { BagView, SampleView, StoreState } from '../projection'
import {
  checkLoadBag,
  checkLoadSample,
  dispatchBlocks,
  isReportedRefusal,
  lotLoadCounts,
  type TripDetails,
} from '../rules'
import { BagLine, Counter, ScanButton, YesNo } from '../components/bits'
import { useRefusalText } from '../components/useRefusalText'
import { StoreScreen } from '../components/StoreScreen'
import { useDraftState, useNow } from '../useStore'

interface LoadDraft {
  step: number
  shipment_id: string
  details: TripDetails
  bags: Array<{ container_id: string; loaded_at: string; item_id: string }>
  samples: Array<{ sample_id: string; loaded_at: string; item_id: string }>
  refused: Array<{ qr_payload: string; reason: ScanRefusal; scanned_at: string }>
  hand_count: number | null
  photo_id: string | null
  confirmed_partial: string[]
}

const newDraft = (defaultDest: string | null): LoadDraft => ({
  step: 0,
  shipment_id: newId(),
  details: {
    to_location_id: defaultDest,
    vehicle_no: '',
    driver_name: '',
    driver_phone: '',
    challan_no: '',
    vehicle_clean: null,
  },
  bags: [],
  samples: [],
  refused: [],
  hand_count: null,
  photo_id: null,
  confirmed_partial: [],
})

/** F5.1 — Load and scan out. The loading list is a draft on the phone until Dispatch. */
export function LoadPage() {
  return (
    <StoreScreen title="store.load">
      {(state) => <LoadFlow state={state} />}
    </StoreScreen>
  )
}

function LoadFlow({ state }: { state: StoreState }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const navigate = useNavigate()
  const actor = useActor()
  const device = useDeviceInfo()
  const now = useNow()
  const refusal = useRefusalText()
  const from = state.siteId
  const defaultDest = state.cache.destinations.find((d) => d.default)?.id ?? null
  const [draft, update, loaded, clear] = useDraftState<LoadDraft>(
    `store.load:${from}`,
    'store.load',
    () => newDraft(defaultDest),
    actor.user_id,
  )
  const [saving, setSaving] = useState(false)
  const [removeMode, setRemoveMode] = useState(false)

  const listed: BagView[] = useMemo(
    () => draft.bags.map((b) => state.bags.get(b.container_id)).filter((b): b is BagView => !!b),
    [draft.bags, state.bags],
  )
  const listedSamples: SampleView[] = draft.samples
    .map((s) => state.samples.get(s.sample_id))
    .filter((s): s is SampleView => !!s)
  const lotCounts = useMemo(() => lotLoadCounts(state, from, listed), [state, from, listed])
  const partial = lotCounts.filter((c) => c.left > 0).map((c) => c.unit_id)
  const kgGate = listed.reduce((sum, b) => sum + (b.net_g_gate ?? 0), 0)
  const ctx = {
    fromLocationId: from,
    vehicleClean: draft.details.vehicle_clean === true,
    onList: new Set(draft.bags.map((b) => b.container_id)),
    samplesOnList: new Set(draft.samples.map((s) => s.sample_id)),
  }
  const blocks = dispatchBlocks({
    fromLocationId: from,
    details: draft.details,
    bagCount: listed.length,
    handCount: draft.hand_count,
    photoId: draft.photo_id,
    partialLots: partial,
    confirmedPartial: new Set(draft.confirmed_partial),
  })

  const steps = [
    { id: 'trip', label: t('store.load.stepTrip') },
    { id: 'bed', label: t('store.load.stepBed') },
    { id: 'scan', label: t('store.load.stepScan') },
    { id: 'dispatch', label: t('store.load.stepDispatch') },
  ]
  const setDetails = (patch: Partial<TripDetails>) =>
    update((d) => ({ ...d, details: { ...d.details, ...patch } }))
  const go = (step: number) => update((d) => ({ ...d, step }))

  const report = (code: string, reason: ScanRefusal) => {
    if (!isReportedRefusal(reason)) return
    update((d) =>
      d.refused.some((r) => r.qr_payload === code)
        ? d
        : {
            ...d,
            refused: [
              ...d.refused,
              { qr_payload: code, reason, scanned_at: new Date().toISOString() },
            ],
          },
    )
  }

  const validateBag = (code: string) => {
    const v = checkLoadBag(state, code, ctx)
    if (v.ok) return null
    if (v.reason !== 'DUPLICATE') report(code, v.reason)
    return refusal(v.reason)
  }
  const addBag = (code: string) => {
    const v = checkLoadBag(state, code, ctx)
    if (!v.ok) return
    const at = new Date().toISOString()
    update((d) =>
      d.bags.some((b) => b.container_id === v.value.container_id)
        ? d
        : {
            ...d,
            bags: [
              ...d.bags,
              { container_id: v.value.container_id, loaded_at: at, item_id: newId() },
            ],
          },
    )
  }
  const validateSample = (code: string) => {
    const v = checkLoadSample(state, code, ctx)
    if (v.ok) return null
    if (v.reason !== 'DUPLICATE') report(code, v.reason)
    return refusal(v.reason)
  }
  const addSample = (code: string) => {
    const v = checkLoadSample(state, code, ctx)
    if (!v.ok) return
    const at = new Date().toISOString()
    update((d) =>
      d.samples.some((s) => s.sample_id === v.value.sample_id)
        ? d
        : {
            ...d,
            samples: [
              ...d.samples,
              { sample_id: v.value.sample_id, loaded_at: at, item_id: newId() },
            ],
          },
    )
  }
  const removeBag = (containerId: string) =>
    update((d) => ({ ...d, bags: d.bags.filter((b) => b.container_id !== containerId) }))

  const dispatch = async () => {
    if (blocks.length) return
    setSaving(true)
    try {
      const prefix = device.registration?.code_prefix ?? 'XX'
      const nn = await nextDailyCounter('shipment')
      const code = `MV-${istYYMMDD()}-${prefix}-${String(nn).padStart(2, '0')}`
      const d = draft
      const load: LoadBagsPayload = {
        shipment: {
          id: d.shipment_id,
          code,
          kind: 'TRANSFER',
          from_location_id: from,
          to_location_id: d.details.to_location_id!,
          vehicle_no: d.details.vehicle_no.trim().toUpperCase(),
          driver_name: d.details.driver_name.trim(),
          driver_phone: d.details.driver_phone,
          challan_no: d.details.challan_no.trim(),
          vehicle_clean: d.details.vehicle_clean === true,
        },
        items: [
          ...d.bags.map((b) => ({
            id: b.item_id,
            qr_payload: state.bags.get(b.container_id)!.qr_payload,
            container_id: b.container_id,
            loaded_at: b.loaded_at,
          })),
          ...d.samples.map((s) => ({
            id: s.item_id,
            qr_payload: state.samples.get(s.sample_id)!.seal_payload,
            sample_id: s.sample_id,
            loaded_at: s.loaded_at,
          })),
        ],
        refused_scans: d.refused,
      }
      const dispatchPayload: DispatchShipmentPayload = {
        shipment_id: d.shipment_id,
        hand_count: d.hand_count ?? 0,
        photo_id: d.photo_id!,
        partial_unit_ids: partial,
        dispatched_at: new Date().toISOString(),
      }
      // A bag changed on this phone by an unsynced command (e.g. a move) must reach the server first.
      const parents = [
        ...new Set(listed.map((b) => b.pending_command_id).filter((x): x is string => !!x)),
      ]
      const cmds: EnqueueInput[] = [
        { type: 'load_bags', payload: load, actorId: actor.user_id, parentIds: parents },
        // depends_on the load_bags command (enqueueCommands chains them).
        {
          type: 'dispatch_shipment',
          payload: dispatchPayload,
          actorId: actor.user_id,
          mediaIds: [d.photo_id!],
        },
      ]
      await enqueueCommands(cmds)
      await clear()
      toast.success(t('store.load.dispatched', { code }), t('store.load.copyTotals'))
      navigate('/m')
    } catch (e) {
      toast.error(t('store.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null
  const destOptions = state.cache.destinations
    .filter((d) => d.id !== from)
    .map((d) => ({ value: d.id, label: d.name, description: `${d.code} · ${d.kind}` }))

  return (
    <div className="flex flex-col gap-4">
      <Stepper steps={steps} current={draft.step} />

      {draft.step === 0 && (
        <Card>
          <CardBody className="flex flex-col gap-4">
            <Field label={<BilingualLabel k="store.load.destination" />} required>
              <PickerSheet
                title={t('store.load.destination')}
                options={destOptions}
                value={draft.details.to_location_id}
                onChange={(v) => setDetails({ to_location_id: v })}
              />
            </Field>
            <Field label={<BilingualLabel k="store.load.vehicleNo" />} required>
              <Input
                size="touch"
                value={draft.details.vehicle_no}
                autoCapitalize="characters"
                maxLength={20}
                className="font-mono uppercase"
                onChange={(e) => setDetails({ vehicle_no: e.target.value })}
              />
            </Field>
            <Field label={<BilingualLabel k="store.load.driverName" />} required>
              <Input
                size="touch"
                value={draft.details.driver_name}
                maxLength={80}
                onChange={(e) => setDetails({ driver_name: e.target.value })}
              />
            </Field>
            <Field
              label={<BilingualLabel k="store.load.driverPhone" />}
              hint={t('store.load.driverPhoneHint')}
              error={
                draft.details.driver_phone && !/^\d{10}$/.test(draft.details.driver_phone)
                  ? t('store.load.driverPhoneHint')
                  : undefined
              }
              required
            >
              <Input
                size="touch"
                inputMode="numeric"
                value={draft.details.driver_phone}
                className="font-mono"
                onChange={(e) =>
                  setDetails({ driver_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })
                }
              />
            </Field>
            <Field
              label={<BilingualLabel k="store.load.challanNo" />}
              hint={t('store.load.challanHint')}
              required
            >
              <Input
                size="touch"
                value={draft.details.challan_no}
                maxLength={30}
                className="font-mono"
                onChange={(e) => setDetails({ challan_no: e.target.value })}
              />
            </Field>
            <Button
              size="xl"
              fullWidth
              disabled={
                !draft.details.to_location_id ||
                !draft.details.vehicle_no.trim() ||
                !draft.details.driver_name.trim() ||
                !/^\d{10}$/.test(draft.details.driver_phone) ||
                !draft.details.challan_no.trim()
              }
              onClick={() => go(1)}
            >
              {t('common.continue')}
            </Button>
          </CardBody>
        </Card>
      )}

      {draft.step === 1 && (
        <Card>
          <CardBody className="flex flex-col gap-4">
            <BilingualLabel
              k="store.load.bedClean"
              layout="stacked"
              className="text-[17px] font-semibold"
            />
            <YesNo
              value={draft.details.vehicle_clean}
              disabled={draft.bags.length > 0}
              onChange={(v) => setDetails({ vehicle_clean: v })}
            />
            {draft.bags.length > 0 && (
              <p className="text-[13px] text-muted">{t('store.load.bedLocked')}</p>
            )}
            {draft.details.vehicle_clean === false && (
              <Banner tone="error" title={t('store.load.bedDirtyTitle')}>
                {t('store.load.bedDirtyBody')}
              </Banner>
            )}
            <div className="flex gap-2">
              <Button variant="secondary" size="xl" onClick={() => go(0)}>
                {t('common.back')}
              </Button>
              <Button
                size="xl"
                fullWidth
                disabled={draft.details.vehicle_clean === null}
                onClick={() => go(2)}
              >
                {t('common.continue')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {draft.step === 2 && (
        <>
          {draft.details.vehicle_clean === false && (
            <Banner tone="warn">{t('store.load.onlyOrdinary')}</Banner>
          )}
          <div className="flex gap-2">
            <Counter label={t('store.load.bagsOn')} value={listed.length} />
            <Counter label={t('store.load.samplesOn')} value={listedSamples.length} />
          </div>
          <ScanButton
            label={<BilingualLabel k="store.load.scanBag" />}
            validate={validateBag}
            onScan={addBag}
          />
          <ScanButton
            variant="secondary"
            label={<BilingualLabel k="store.load.scanSample" />}
            validate={validateSample}
            onScan={addSample}
          />
          {lotCounts.length > 0 && (
            <Card>
              <CardHeader eyebrow={t('store.lots')} />
              <CardBody className="py-2">
                <ul>
                  {lotCounts.map((c) => (
                    <li
                      key={c.unit_id}
                      className="flex items-center justify-between gap-2 border-b border-rule/70 py-2 last:border-b-0"
                    >
                      <span className="font-mono text-[14px]">
                        {state.lots.get(c.unit_id)?.code}
                      </span>
                      <span className="font-mono text-[14px] tabular">
                        {t('store.nOfMOn', { n: c.on, m: c.on + c.left })}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
          {listed.length > 0 && (
            <Card>
              <CardHeader
                eyebrow={t('store.load.list')}
                actions={
                  <Button
                    size="sm"
                    variant={removeMode ? 'danger' : 'ghost'}
                    onClick={() => setRemoveMode((m) => !m)}
                  >
                    {t('store.removeBag')}
                  </Button>
                }
              />
              <CardBody className="py-1">
                {removeMode && (
                  <div className="py-2">
                    <ScanButton
                      variant="secondary"
                      label={<BilingualLabel k="store.load.scanToRemove" />}
                      validate={(c) =>
                        listed.some((b) => b.qr_payload === c) ? null : refusal('TAG_UNKNOWN')
                      }
                      onScan={(c) => {
                        const b = listed.find((x) => x.qr_payload === c)
                        if (b) removeBag(b.container_id)
                      }}
                    />
                  </div>
                )}
                <ul>
                  {[...listed].reverse().map((b) => (
                    <BagLine
                      key={b.container_id}
                      state={state}
                      bag={b}
                      now={now}
                      onRemove={removeMode ? () => removeBag(b.container_id) : undefined}
                    />
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
          {draft.refused.length > 0 && (
            <Banner tone="warn" title={t('store.refusedReported', { count: draft.refused.length })}>
              {draft.refused.map((r) => r.qr_payload).join(', ')}
            </Banner>
          )}
          <div className="flex gap-2">
            <Button variant="secondary" size="xl" onClick={() => go(1)}>
              {t('common.back')}
            </Button>
            <Button size="xl" fullWidth disabled={listed.length === 0} onClick={() => go(3)}>
              {t('common.continue')}
            </Button>
          </div>
        </>
      )}

      {draft.step === 3 && (
        <>
          <Card>
            <CardHeader eyebrow={<BilingualLabel k="store.load.totals" />} />
            <CardBody className="py-1">
              <KeyValue
                items={[
                  { label: t('store.lots'), value: lotCounts.length, mono: true },
                  { label: t('store.bags'), value: listed.length, mono: true },
                  { label: t('store.samples'), value: listedSamples.length, mono: true },
                  { label: t('store.kgAtGate'), value: fmt.weight(kgGate), mono: true },
                  { label: t('store.load.challanNo'), value: draft.details.challan_no, mono: true },
                ]}
              />
              <p className="pb-2 text-[13px] text-muted">{t('store.load.copyTotals')}</p>
            </CardBody>
          </Card>
          {partial.length > 0 && (
            <Card>
              <CardHeader eyebrow={t('store.load.partialTitle')} />
              <CardBody className="flex flex-col gap-2">
                {lotCounts
                  .filter((c) => c.left > 0)
                  .map((c) => {
                    const ok = draft.confirmed_partial.includes(c.unit_id)
                    return (
                      <Button
                        key={c.unit_id}
                        size="touch"
                        variant={ok ? 'secondary' : 'accent'}
                        fullWidth
                        onClick={() =>
                          update((d) => ({
                            ...d,
                            confirmed_partial: ok
                              ? d.confirmed_partial.filter((u) => u !== c.unit_id)
                              : [...d.confirmed_partial, c.unit_id],
                          }))
                        }
                      >
                        {ok ? '✓ ' : ''}
                        {t('store.load.partialConfirm', {
                          lot: state.lots.get(c.unit_id)?.code ?? '',
                          left: c.left,
                        })}
                      </Button>
                    )
                  })}
              </CardBody>
            </Card>
          )}
          <Card>
            <CardBody className="flex flex-col gap-4">
              <Field
                label={<BilingualLabel k="store.load.handCount" />}
                hint={t('store.load.handCountHint')}
                error={
                  draft.hand_count !== null && draft.hand_count !== listed.length
                    ? t('store.load.handCountMismatch', { scanned: listed.length })
                    : undefined
                }
                required
              >
                <IntegerInput
                  size="touch"
                  value={draft.hand_count}
                  onChange={(n) => update((d) => ({ ...d, hand_count: n }))}
                  maxDigits={4}
                />
              </Field>
              <Field
                label={<BilingualLabel k="store.load.photo" />}
                hint={t('store.load.photoHint')}
                required
              >
                <PhotoCapture
                  value={draft.photo_id}
                  onChange={(id) => update((d) => ({ ...d, photo_id: id }))}
                />
              </Field>
            </CardBody>
          </Card>
          {blocks.length > 0 && (
            <Banner tone="warn" title={t('store.load.lockedTitle')}>
              <ul className="list-inside list-disc">
                {blocks.map((b) => (
                  <li key={b}>{t(`store.load.block.${b}` as const)}</li>
                ))}
              </ul>
            </Banner>
          )}
          <div className="flex gap-2">
            <Button variant="secondary" size="xl" onClick={() => go(2)}>
              {t('common.back')}
            </Button>
            <Button
              size="xl"
              variant="accent"
              fullWidth
              leadingIcon={
                blocks.length ? <Warning aria-hidden="true" /> : <Truck aria-hidden="true" />
              }
              disabled={blocks.length > 0}
              loading={saving}
              onClick={() => void dispatch()}
            >
              <BilingualLabel k="store.load.dispatch" />
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
